import { Checkbox, Form, Input, Select } from 'antd';
import { BankAccountsGroup } from '@/components/bank-accounts-group';
import JobPositionFormFields from '@/components/job-positions/JobPositionFormFields';
import { useEmployeeGeneralDataForm } from '../EmployeeGeneralDataFormContext';

/**
 * Company, status, personal, contact and bank fields. On the profile the
 * personal and contact fields are laid out by EmployeePersonalProfilePanel,
 * the bank fields by EmployeePaymentProfilePanel, company and position by
 * EmployeeOrgProfilePanel.
 */
export default function EmployeePersonalFields() {
  const {
    addressKeys,
    addressState,
    bankAccountsKeys,
    bankAccountsState,
    bankNameKeys,
    bankNameState,
    cityKeys,
    cityState,
    companyIdKeys,
    companyIdState,
    companyIsLoading,
    companyOptions,
    companyOptionsData,
    countryKeys,
    countryState,
    emailKeys,
    emailState,
    employeeSectorKeys,
    firstNameKeys,
    firstNameState,
    form,
    inSection,
    isProfile,
    isResidentKeys,
    isResidentState,
    lastNameKeys,
    lastNameState,
    municipalityKeys,
    municipalityState,
    personalNumberKeys,
    personalNumberState,
    phoneKeys,
    phoneState,
    positionEngKeys,
    positionEngState,
    positionKeys,
    positionState,
    selectedCompany,
    statusKeys,
    statusOptions,
    statusState,
    t,
  } = useEmployeeGeneralDataForm();

  return (
    <>
      <div className="grid grid-cols-2 gap-x-4">
        {!isProfile && companyIdState?.visible && (
          <Form.Item
            hidden={!inSection('org')}
            name={companyIdKeys}
            label={t(
              companyOptionsData?.isSingleCompany ? 'company' : 'selectCompany',
              { ns: 'forms' }
            )}
            rules={[
              {
                required: true,
                message: t('formValidation.companyRequired', { ns: 'forms' }),
              },
            ]}
          >
            <Select
              allowClear
              showSearch
              mode="single"
              placeholder={t('selectCompany', { ns: 'forms' })}
              className="w-full"
              optionFilterProp="label"
              disabled={
                companyOptionsData?.isSingleCompany || companyIdState?.disabled
              }
              loading={companyIsLoading}
              options={companyOptions}
              // Sectors belong to one company: a sector kept from the previous
              // company is not among the new options and would be saved as is.
              onChange={() => form.setFieldValue(employeeSectorKeys, undefined)}
            />
          </Form.Item>
        )}

        {!isProfile && statusState?.visible && (
          <Form.Item
            hidden={!inSection('ugovor')}
            label={t('status', { ns: 'tables' })}
            name={statusKeys}
            rules={[
              {
                required: true,
                message: t('formValidation.statusRequired', {
                  ns: 'forms',
                }),
              },
            ]}
          >
            <Select
              allowClear
              mode="single"
              placeholder={t('status', { ns: 'tables' })}
              className="w-full"
              options={statusOptions}
              disabled={statusState?.disabled}
            />
          </Form.Item>
        )}

        {!isProfile && firstNameState?.visible && (
          <Form.Item
            hidden={!inSection('licno')}
            label={t('firstName', { ns: 'forms' })}
            name={firstNameKeys}
            rules={[
              {
                required: true,
                message: t('formValidation.firstNameRequired', { ns: 'forms' }),
              },
              {
                type: 'firstName',
                message: t('formValidation.firstNameInvalid', { ns: 'forms' }),
              },
            ]}
          >
            <Input
              placeholder={t('firstName', { ns: 'forms' })}
              disabled={firstNameState?.disabled}
            />
          </Form.Item>
        )}

        {!isProfile && lastNameState?.visible && (
          <Form.Item
            hidden={!inSection('licno')}
            label={t('lastName', { ns: 'forms' })}
            name={lastNameKeys}
            rules={[
              {
                required: true,
                message: t('formValidation.lastNameRequired', { ns: 'forms' }),
              },
              {
                type: 'lastName',
                message: t('formValidation.lastNameInvalid', { ns: 'forms' }),
              },
            ]}
          >
            <Input
              placeholder={t('lastName', { ns: 'forms' })}
              disabled={lastNameState?.disabled}
            />
          </Form.Item>
        )}

        {!isProfile && emailState?.visible && (
          <Form.Item
            hidden={!inSection('licno')}
            name={emailKeys}
            label={t('email', { ns: 'forms' })}
            rules={[
              {
                required: false,
                message: t('formValidation.emailRequired', { ns: 'forms' }),
              },
              {
                type: 'email',
                message: t('formValidation.emailInvalid', { ns: 'forms' }),
              },
            ]}
          >
            <Input
              placeholder={t('email', { ns: 'forms' })}
              disabled={emailState?.disabled}
            />
          </Form.Item>
        )}

        {!isProfile && personalNumberState?.visible && (
          <Form.Item
            hidden={!inSection('licno')}
            name={personalNumberKeys}
            label={t('personalNumber', { ns: 'forms' })}
            rules={[
              {
                required: true,
                message: t('formValidation.personalNumberRequired', {
                  ns: 'forms',
                }),
              },
            ]}
          >
            <Input
              disabled={personalNumberState?.disabled}
              placeholder={t('personalNumber', { ns: 'forms' })}
            />
          </Form.Item>
        )}

        {!isProfile && countryState?.visible && (
          <Form.Item
            hidden={!inSection('licno')}
            label={t('country', { ns: 'forms' })}
            name={countryKeys}
          >
            <Input
              disabled={countryState?.disabled}
              placeholder={t('country', { ns: 'forms' })}
            />
          </Form.Item>
        )}

        {!isProfile && addressState?.visible && (
          <Form.Item
            hidden={!inSection('licno')}
            label={t('address', { ns: 'forms' })}
            name={addressKeys}
          >
            <Input
              disabled={addressState?.disabled}
              placeholder={t('address', { ns: 'forms' })}
            />
          </Form.Item>
        )}

        {!isProfile && cityState?.visible && (
          <Form.Item
            hidden={!inSection('licno')}
            label={t('city', { ns: 'forms' })}
            name={cityKeys}
          >
            <Input
              disabled={cityState?.disabled}
              placeholder={t('city', { ns: 'forms' })}
            />
          </Form.Item>
        )}

        {!isProfile && municipalityState?.visible && (
          <Form.Item
            hidden={!inSection('licno')}
            label={t('municipality', { ns: 'forms' })}
            name={municipalityKeys}
          >
            <Input
              disabled={municipalityState?.disabled}
              placeholder={t('municipality', { ns: 'forms' })}
            />
          </Form.Item>
        )}

        {!isProfile && (
          <JobPositionFormFields
            hidden={!inSection('org')}
            form={form}
            companyId={selectedCompany}
            positionKeys={positionKeys}
            positionEngKeys={positionEngKeys}
            positionState={positionState}
            positionEngState={positionEngState}
          />
        )}

        {!isProfile && phoneState?.visible && (
          <Form.Item
            hidden={!inSection('licno')}
            name={phoneKeys}
            label={t('phoneNumber', { ns: 'forms' })}
          >
            <Input
              disabled={phoneState?.disabled}
              type="number"
              placeholder={t('phoneNumber', { ns: 'forms' })}
              className="[-moz-appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
          </Form.Item>
        )}

        {!isProfile && bankNameState?.visible && (
          <Form.Item
            hidden={!inSection('isplata')}
            name={bankNameKeys}
            label={t('bankName', { ns: 'forms' })}
          >
            <Input
              disabled={bankNameState?.disabled}
              placeholder={t('bankName', { ns: 'forms' })}
            />
          </Form.Item>
        )}
      </div>

      {!isProfile && bankAccountsState?.visible && (
        <Form.Item
          hidden={!inSection('isplata')}
          label={t('bankAccountNumber', { ns: 'forms' })}
          name={bankAccountsKeys}
        >
          <BankAccountsGroup
            form={form}
            name={bankAccountsKeys[1]}
            nestedName={bankAccountsKeys[0]}
            disabled={bankAccountsState?.disabled}
          />
        </Form.Item>
      )}

      {!isProfile && isResidentState?.visible && (
        <Form.Item
          hidden={!inSection('isplata')}
          name={isResidentKeys}
          label={null}
          valuePropName="checked"
          className="!mb-4"
        >
          <Checkbox disabled={isResidentState?.disabled}>
            {t('resident', { ns: 'forms' })}
          </Checkbox>
        </Form.Item>
      )}
    </>
  );
}
